import { Spacer } from '@sparemin/blockhead';
import React from 'react';

import DialogContent from 'components/DialogContent';
import { H2 } from 'components/Heading';
import LoadingOverlay from 'components/LoadingOverlay';
import ModalFooterButton from 'components/Modal/ModalFooterButton';
import SocialAccountInfo from 'components/SocialAccountInfo';
import { Step } from 'components/SteppedModal';
import useVerifyYouTubeAccount from 'hooks/useVerifyYouTubeAccount';

import { YouTubeAccountVerificationCheckSource } from 'redux/modules/mixpanel/types';
import useYoutubePostModalSelector from '../useYoutubePostModalSelector';
import { block } from '../utils';

export interface UseVerificationStepConfig {
  source: YouTubeAccountVerificationCheckSource;
  onAccountDisallowed: () => void;
  onAccountVerified: () => void;
  onSwitchAccount: () => void;
}

const useVerificationStep = (config: UseVerificationStepConfig): Step => {
  const {
    source,
    onAccountDisallowed,
    onAccountVerified,
    onSwitchAccount,
  } = config;

  const { isVerifying, onVerifyAccount } = useVerifyYouTubeAccount({
    source,
    onAccountVerified,
    onAccountDisallowed,
  });

  const { avatarSrc, username } = useYoutubePostModalSelector();

  return {
    title: 'awaiting verification',
    modalClassName: block('dialog-content'),
    component: (
      <>
        {isVerifying && <LoadingOverlay small title="Verifying..." />}
        <DialogContent
          className={block('dialog')}
          details={
            <H2>
              After verification we can continue getting ready for YouTube.
            </H2>
          }
          detailsClassName={block('dialog-details')}
          headerRow={
            <Spacer>
              <SocialAccountInfo
                avatarSrc={avatarSrc}
                onSwitchAccountClick={onSwitchAccount}
                username={username}
              />
            </Spacer>
          }
          onClose={() => null}
          title="Tell us when you’re verified"
          titleClassName={block('dialog-title')}
        />
      </>
    ),
    fluidFooterButtons: true,
    id: 'verify-account',
    renderFooterButtons: ({ submit }) => [
      <ModalFooterButton
        {...submit}
        disabled={isVerifying}
        key="submit"
        onClick={onVerifyAccount}
        size="large"
        theme="submit"
      >
        my account is now verified
      </ModalFooterButton>,
    ],
  };
};

export default useVerificationStep;
